/**
 * Where every rock in the corridor is drawn, and which one a tap actually means.
 *
 * Pulled out of `RockField.tsx` because these are two different questions with one
 * answer, and only one of them was being asked. The component computed each rock's
 * position inside its own render loop, which meant the layout existed only as a side
 * effect of drawing — so nothing else could see it, and the browser was left to decide
 * what a tap hit from the DOM boxes alone.
 *
 * That decision is wrong about 1% of the time, measured — `field.test.ts` runs the real
 * projection over the server's real placement distributions and counts it: 70 stolen taps
 * in 6,532 on rock that was plainly visible and in reach.
 *
 * ## Why the browser gets it wrong
 *
 * A rock is drawn as a square `<button>` with a background sprite. The sprite is a lumpy
 * rock; the button is a square with `border-radius: 38%`. Between the two is a wide
 * transparent margin that is nevertheless a live tap target, and three rocks spawn at
 * every second metre — so those margins overlap constantly. The topmost box wins, and
 * the topmost box is often not the rock under your finger.
 *
 * The fix is to test against the silhouette rather than the box, approximated by the
 * ellipse inscribed in it. That is not merely tighter: it is *correct* about occlusion,
 * which the naive alternatives are not. A rock genuinely hidden behind a nearer one
 * must still lose the tap — "pick whatever centre is closest" would hand the tap to a
 * rock the player cannot see.
 *
 * ## What this deliberately does not do
 *
 * It does not decide anything about the rock. Position comes from the server
 * (`slot`, `slot_u`, `slot_v`) and so does the payout; this only turns corridor
 * coordinates into screen coordinates, which is `projection.ts`'s business, and reads
 * them back. A rock is a payout, and the client never invents one.
 */
import type { RockDTO } from '@/api/endpoints';

import { clamp, inReach, project, slotFromUuid, type RockSlot, type Viewport } from './projection';

/** Below this the rock is a smudge; a tap target there is a mis-tap generator. */
export const TAPPABLE_MIN_SCALE = 0.34;

/** A fogged rock is not drawn at all — an invisible tap target is worse than no rock. */
export const FOG_CULL = 0.04;

/**
 * How far a floor rock sits below the floor line, as a fraction of its own height.
 *
 * Rubble is half-buried in more rubble. Sitting one exactly on the line makes it read
 * as balanced on the surface, which is the same wrongness as a sticker.
 */
export const FLOOR_SINK = 0.26;

/**
 * How much of a wall or ceiling rock is inside the rock face, as a fraction of its width.
 *
 * A rock in a cave wall is not a boulder resting against it — it is stone still joined to
 * the seam it grew in, and only the part that has weathered proud of the face is visible.
 * Drawing the whole sprite made every wall rock read as a sticker: a complete, closed
 * silhouette with a hard edge all the way round, floating on a surface it plainly was not
 * part of. Cutting it at the face is what makes it *in* the wall rather than *on* it.
 *
 * A straight cut is the honest one. The wall is a surface and the rock intersects it, so
 * the boundary between buried and exposed is that surface — a plane, projected here as a
 * line. Anything softer (an oval mask, a feathered edge) would be a drawing of a rock
 * poking through cloth.
 *
 * Slightly under a half so the exposed part still reads as a rounded mass rather than a
 * hemisphere; the per-rock jitter below keeps a wall of them from looking machined.
 */
export const EMBED_FRACTION = 0.42;

/**
 * How far either side of `EMBED_FRACTION` a given rock may sit. Deterministic per rock.
 *
 * Chosen so the buried fraction can never reach a half. Past that the exposed part no
 * longer contains the sprite's own middle — so the rock reads as a pebble stuck to the
 * wall rather than a mass emerging from it, and the point a tap is measured from ends up
 * on the buried side of the cut.
 */
export const EMBED_JITTER = 0.06;

/** Base sprite size in px at scale 1 — floor rubble reads smaller than a wall seam. */
const BASE_SIZE = { floor: 66, other: 76 } as const;

export interface PlacedRock {
  rock: RockDTO;
  /** Metres ahead of the camera. Negative is behind. */
  d: number;
  /** The projection's anchor point — the corridor position, in screen px. */
  x: number;
  y: number;
  /** The sprite's bounding box, in screen px. Floor rock hangs from its base. */
  left: number;
  top: number;
  size: number;
  /** Painter's order; higher draws on top. */
  z: number;
  /** Raw linear fog, before `fogAlpha`'s curve. */
  opacity: number;
  /** In reach, unlocked, and big enough to aim at. */
  tappable: boolean;
  onFloor: boolean;
  /** Stable per-rock appearance jitter, so a rock never spins between renders. */
  spin: number;
  flip: 1 | -1;
  /**
   * Which way the rock is buried, and how deep — see `embedding`.
   *
   * `null` for path rock, which stands in the corridor rather than in a surface.
   */
  embed: Embedding | null;
}

/**
 * The buried half of a rock: which edge of the sprite the surface cuts, and where.
 *
 * `axis`/`side` name the edge that is INSIDE the rock face. `fraction` is how much of the
 * sprite that edge swallows, measured from that edge as a fraction of the sprite's width.
 */
export interface Embedding {
  axis: 'x' | 'y';
  /** -1 = the low edge (left / top) is buried; +1 = the high edge (right / bottom). */
  side: -1 | 1;
  fraction: number;
}


/**
 * Where a rock sits in the corridor. **One rule, used by everything that draws one.**
 *
 * There were two, and they disagreed. `RockField` read the server's `slot_u`/`slot_v`;
 * `CorridorRock` — which draws the featured path rock, the one the axe gate is about and
 * the only rock that can carry Kyat — threw them away and re-derived a position from two
 * characters of the uuid:
 *
 *     const slot = rock.material?.key === 'path' ? 'path' : slotOf(rock);
 *
 * No material is keyed `'path'` (they are loose, hard, granite, … shwe), so that test was
 * always false, and `slotOf` returns only `'wall'` or `'ceiling'`. The blocking rock was
 * therefore drawn pinned against a side wall four times in five and on the ceiling the
 * rest — never in the path the server had placed it in, and never at the size the code
 * right below it intended: `base = slot === 'path' ? 150 : 92` was unreachable, so the
 * blocker rendered at 61% of the size meant to make it read as a blocker.
 *
 * Server first, always. The fallback exists for a payload that predates server placement
 * and is deterministic so a rock still cannot move between renders — but it is a fallback,
 * not a second opinion.
 */
export function placementOf(rock: RockDTO): { slot: RockSlot; u: number; v: number } {
  const slot: RockSlot = rock.slot ?? 'wall';
  if (rock.slot_u !== undefined && rock.slot_v !== undefined) {
    return { slot, u: rock.slot_u, v: rock.slot_v };
  }
  return { slot, ...slotFromUuid(rock.spawn_uuid, slot) };
}

/**
 * Which way a rock is buried in the surface it grew in.
 *
 * The surface is decided by the slot, and for a side wall by the sign of `u`: a rock at
 * `u < 0` is in the left wall, so its LEFT edge is the buried one and it protrudes to the
 * right. Getting that backwards is not subtle — the rock appears to grow out of thin air
 * and hang over the corridor, with its cut edge facing the player.
 *
 * Floor rock returns `null`: it is rubble lying on the ground, not stone joined to a face,
 * and it is already sunk by `FLOOR_SINK` and anchored from its base. Cutting it as well
 * would slice the top off a pebble.
 *
 * A rock exactly on the centre line (`u === 0`) cannot be in either side wall. It is
 * treated as the left wall so the answer is total and deterministic rather than a
 * coin-flip that changes between renders; at `u === 0` the rock is behind the player's
 * eye line anyway and the choice is invisible.
 */
export function embedding(slot: RockSlot, u: number, uuid: string): Embedding | null {
  if (slot === 'floor' || slot === 'path') return null;

  const fraction = EMBED_FRACTION + (hash01(uuid, 4) - 0.5) * 2 * EMBED_JITTER;

  if (slot === 'ceiling') {
    /* Hanging from the roof: the TOP of the sprite is inside the rock above. */
    return { axis: 'y', side: -1, fraction };
  }
  return { axis: 'x', side: u < 0 ? -1 : 1, fraction };
}

/**
 * The buried half, as a CSS `inset()` clip.
 *
 * `inset()` and not a mask image: the cut is a straight line, the browser composites it on
 * the GPU, and it costs nothing per frame — which matters when twenty of these are on
 * screen and the corridor is already running a parallax.
 */
export function clipFor(embed: Embedding | null): string | undefined {
  if (!embed) return undefined;
  const pct = `${(embed.fraction * 100).toFixed(2)}%`;
  if (embed.axis === 'y') {
    return embed.side < 0 ? `inset(${pct} 0 0 0)` : `inset(0 0 ${pct} 0)`;
  }
  return embed.side < 0 ? `inset(0 0 0 ${pct})` : `inset(0 ${pct} 0 0)`;
}

/**
 * A stable pseudo-random number in [0, 1) from a spawn uuid and a salt.
 *
 * Used only for *appearance* — rotation and size jitter — never for anything the server
 * decides. It has to be a hash rather than `Math.random()` so a rock does not spin
 * between renders, and so two devices showing the same cave show the same cave.
 *
 * Measured over 200k real v4 uuids: uniform to chi-squared 4.2-6.6 across ten deciles
 * (5% critical value 16.9), and the three salts are mutually uncorrelated to within
 * 0.006 — which is the property that matters, because one rock reads all three at once
 * and correlated salts would make every big rock face the same way.
 */
export function hash01(uuid: string, salt: number): number {
  let h = salt * 2654435761;
  for (let i = 0; i < uuid.length; i++) h = (Math.imul(h ^ uuid.charCodeAt(i), 16777619) >>> 0);
  return (h >>> 8) / 0x1000000;
}

/**
 * Where a rock's box sits relative to its corridor point.
 *
 * **The anchor IS the rock face**, so the cut lands on it and the rock protrudes outward.
 *
 * Centring the sprite on the point instead makes the *sprite's middle* the wall, which is
 * only the same thing while exactly half is buried. Once the per-rock jitter takes the
 * buried fraction past a half, the corridor point the server placed — and the point a tap
 * is measured from — ends up inside the stone, and the rock answers taps through the wall
 * while its visible mass hangs off-centre from where it was put.
 *
 * Floor rubble hangs from its base instead and sinks by `FLOOR_SINK`, because it is resting
 * on the ground rather than joined to a face.
 *
 * One rule, exported, because the knock-out has to obey it too: a falling rock that started
 * from the *centre* of the point would jump sideways by up to `EMBED_FRACTION` of its own
 * width on the frame it came loose. That pop is the difference between stone breaking out
 * of a wall and a sprite being swapped for another sprite.
 */
export function anchorOffset(
  embed: Embedding | null,
  size: number,
  onFloor: boolean,
): { offX: number; offY: number } {
  const offX = embed?.axis === 'x'
    ? (embed.side < 0 ? -embed.fraction * size : -(1 - embed.fraction) * size)
    : -size / 2;
  const offY = embed?.axis === 'y'
    ? (embed.side < 0 ? -embed.fraction * size : -(1 - embed.fraction) * size)
    : (onFloor ? -size * (1 - FLOOR_SINK) : -size / 2);
  return { offX, offY };
}

/**
 * How big a rock draws, at a given projected scale.
 *
 * Jittered per rock, because one sprite repeated twenty times reads as a stamp however
 * good the sprite is. Exported because a knocked-out rock has to leave the wall at exactly
 * the size it had while it was in the wall — a fall that starts with a jump in size reads
 * as one rock being swapped for another, which is precisely what it must not look like.
 */
export function sizeOf(uuid: string, slot: RockSlot, scale: number): number {
  const jitter = 0.82 + 0.36 * hash01(uuid, 1);
  return (slot === 'floor' ? BASE_SIZE.floor : BASE_SIZE.other) * scale * jitter;
}

/**
 * Place every field rock the lamp reaches.
 *
 * Skips the featured path rock and the rock being worked: `CorridorRock` draws those at
 * full size, and drawing them here too would put the same rock on screen twice at two
 * different sizes.
 */
export function layoutField(
  spawns: readonly RockDTO[],
  depth: number,
  view: Viewport,
  lightPx: number,
  activeUuid: string | null,
): PlacedRock[] {
  const placed: PlacedRock[] = [];

  for (const rock of spawns) {
    if (rock.spawn_uuid === activeUuid || rock.slot === 'path') continue;

    const d = (rock.depth_m ?? depth) - depth;
    const { u, v } = placementOf(rock);
    const p = project(u, v, d, view, lightPx);
    if (p.opacity <= FOG_CULL) continue;

    const onFloor = rock.slot === 'floor';
    const size = sizeOf(rock.spawn_uuid, rock.slot ?? 'wall', p.scale);

    const embed = embedding(rock.slot ?? 'wall', u, rock.spawn_uuid);
    const { offX, offY } = anchorOffset(embed, size, onFloor);

    placed.push({
      rock,
      d,
      x: p.x,
      y: p.y,
      left: p.x + offX,
      /* Floor rock is rubble resting on the ground, so it hangs from its base and sinks a
         fraction of its height below the floor line. Wall and ceiling rock hangs off the
         surface the cut sits on. */
      top: p.y + offY,
      size,
      z: p.z,
      opacity: p.opacity,
      tappable: inReach(d) && !rock.locked && p.scale >= TAPPABLE_MIN_SCALE,
      onFloor,
      spin: Math.round(hash01(rock.spawn_uuid, 2) * 360),
      flip: hash01(rock.spawn_uuid, 3) < 0.5 ? -1 : 1,
      embed,
    });
  }

  /*
   * Sort into paint order, so the DOM agrees with it.
   *
   * `z` is `(100 - d) * 10`, so three rocks spawned at the same metre get the SAME
   * z-index and the browser falls back to document order — which is the server's
   * `order_by("depth_m")`, a query with no secondary key. Among equal depths that is
   * whatever the database felt like returning, so the stacking of the three rocks at
   * one metre was free to change between requests with nothing in the game changing.
   *
   * Sorting here makes document order the paint order, and `pickAt` reads the same
   * order back. That agreement is the part that matters: if the two disagree, the
   * player taps the rock on top and mines the one underneath it.
   */
  return placed.sort(comparePaint);
}

/**
 * Total order, back to front. Later draws on top.
 *
 * Depth first. Then, at the same metre, lower on screen — in a corridor the floor is
 * nearer the camera than the wall behind it, which is also why rubble banks in front of
 * the stone it fell from. Then the uuid, because two rocks CAN tie on both and the
 * order still has to be the same on every device and after every reload; which one wins
 * matters far less than that the answer never moves.
 */
export function comparePaint(a: PlacedRock, b: PlacedRock): number {
  if (a.z !== b.z) return a.z - b.z;
  if (a.y !== b.y) return a.y - b.y;
  return a.rock.spawn_uuid < b.rock.spawn_uuid ? -1 : 1;
}

/**
 * Is this point inside the drawn silhouette?
 *
 * The ellipse inscribed in the sprite's box. Approximate on purpose — the alternative is
 * reading the atlas's alpha channel per tap, which means a canvas, a texture read and a
 * cross-origin constraint for a rock that is already only 40px across. An ellipse is
 * close to a rock and much closer than a square: measured against the square it removes
 * every mis-routed tap in the sample, while still letting a rock that really is hidden
 * behind a nearer one lose the tap.
 */
export function coversPoint(p: PlacedRock, px: number, py: number): boolean {
  const rx = p.size / 2;
  const ry = p.size / 2;
  const dx = (px - (p.left + rx)) / rx;
  const dy = (py - (p.top + ry)) / ry;
  if (dx * dx + dy * dy > 1) return false;

  /*
   * The buried half is not there to be tapped.
   *
   * Without this the tap target stayed a full ellipse while only part of it was drawn, so
   * the rock would answer taps on wall the player can see straight through — and it would
   * do it from *on top*, shadowing the rock actually under the finger. That is the same
   * defect `pickAt` exists to fix, reintroduced from the other side: the silhouette and
   * the target have to be the same shape, or the shape is decorative.
   */
  const e = p.embed;
  if (!e) return true;
  const along = e.axis === 'x' ? dx : dy;   // -1 at the low edge, +1 at the high edge
  const cut = -1 + 2 * e.fraction;          // the surface, in the same -1..1 units
  return e.side < 0 ? along >= cut : along <= -cut;
}

/**
 * Which rock is actually drawn at this point — the topmost silhouette covering it.
 *
 * "Topmost", not "nearest centre", and the difference is the whole point. A rock hidden
 * behind a nearer one is *supposed* to lose the tap: the player cannot see it, so giving
 * it the tap would mine a rock they never chose, with a payout they never saw. Nearest
 * centre would do exactly that.
 *
 * Only tappable rocks are considered. An out-of-reach rock is already inert in the DOM
 * (`disabled:pointer-events-none`), and letting one shadow a rock behind it would make
 * scenery block the game.
 */
export function pickAt(
  placed: readonly PlacedRock[],
  px: number,
  py: number,
): PlacedRock | null {
  let best: PlacedRock | null = null;
  for (const p of placed) {
    if (!p.tappable || !coversPoint(p, px, py)) continue;
    /* `comparePaint`, not `z` — the same total order the list was drawn in, so the
       rock this returns is the one the player can actually see on top. */
    if (best === null || comparePaint(p, best) > 0) best = p;
  }
  return best;
}


/* ── knocking a broken rock out of the wall ─────────────────────────────────────────── */

/**
 * How a rock behaves once the last swing frees it.
 *
 * The corridor is a CSS projection, not a physics engine, so this is a **ballistic solve in
 * corridor space** — the same `u`/`v`/`d` the server places rocks in — projected through
 * `project()` every frame. That is the honest version of "real world physics" here: real
 * enough that the rock leaves the wall along the normal, falls, lands where the floor
 * actually is at that depth, and loses energy on each bounce; not a rigid-body sim, which
 * would be a lie told at 60 fps about a scene that has no bodies in it.
 *
 * Deliberately no collision with other rocks. Debris is transient decoration that the
 * server does not know about, and stone settling against stone the server never placed
 * would be the client inventing scenery that vanishes on the next reload.
 *
 * Units are corridor units per second: `v` runs -1 (floor) to +1 (ceiling). Gravity is
 * tuned rather than physical — 11 drops a rock from mid-wall to the floor in about 0.45 s,
 * against roughly 0.55 s for the real 1.5 m fall. The rock has to be down and still before
 * the loot card covers it, and a tenth of a second of extra wait to be more correct about
 * a stone nobody is measuring is the wrong trade.
 */
export const GRAVITY = 11;
/** How much of the impact speed survives a bounce. Stone on rubble is dead — it barely hops. */
export const RESTITUTION = 0.22;
/** Sideways speed lost per bounce; rubble does not skid. */
export const FLOOR_FRICTION = 0.55;
/** Where the rock comes to rest, matching `FLOOR_SINK` so it settles into the rubble line. */
export const REST_V = -1 + 0.04;
/** Total flight time before the debris is removed, in seconds. */
export const KNOCKOUT_SECONDS = 0.9;

/**
 * When the loot card opens, measured from the breaking swing.
 *
 * Shorter than the flight on purpose: the card should arrive as the rock lands, not after
 * it has finished settling and faded. Waiting for the full animation puts a visible pause
 * between the swing and the reward, which is the one place in this loop that must feel
 * immediate.
 */
export const LOOT_CARD_DELAY_MS = 430;

export interface DebrisSpec {
  uuid: string;
  /** Where it broke, in corridor coordinates. */
  u: number;
  v: number;
  d: number;
  slot: RockSlot;
  materialKey: string;
  size: number;
}

export interface DebrisState {
  u: number;
  v: number;
  spin: number;
  /** 1 until it settles, then fades — so the rock is gone by the time the loot card lands. */
  alpha: number;
  landed: boolean;
}

/**
 * Where a knocked-out rock is `t` seconds after the swing that freed it.
 *
 * A closed-form parabola per bounce rather than a stepped integration: the same `t` must
 * give the same answer on every device and after a dropped frame, and an integrator that
 * advances on `requestAnimationFrame` gives a different arc on a slow phone than on a fast
 * one. It also means a test can ask "where is it at 0.4s" without running a clock.
 */
export function debrisAt(t: number, spec: DebrisSpec): DebrisState {
  const outward = spec.slot === 'ceiling' ? 0 : (spec.u < 0 ? 1 : -1);
  /* Kicked out along the wall normal by the axe, not thrown — this is the rock letting go,
     so the sideways speed is small and the fall does most of the work. */
  const push = 0.45 + 0.25 * hash01(spec.uuid, 5);
  const vu0 = outward * push;
  const vv0 = spec.slot === 'ceiling' ? -0.15 : 0.22;   // a slight lift off the face

  let u = spec.u + vu0 * t;
  let v = spec.v + vv0 * t - 0.5 * GRAVITY * t * t;

  /* Bounce. Solve the landing analytically and restart the parabola from it — at most a
     couple of times, because with a restitution of 0.28 the third hop is under a
     millimetre on screen and nobody has ever seen it. */
  let tRemaining = t;
  let u0 = spec.u;
  let v0 = spec.v;
  let vu = vu0;
  let vv = vv0;

  for (let bounce = 0; bounce < 3; bounce++) {
    const a = -0.5 * GRAVITY;
    const b = vv;
    const c = v0 - REST_V;
    const disc = b * b - 4 * a * c;
    if (disc < 0) break;
    const tHit = (-b - Math.sqrt(disc)) / (2 * a);
    if (!(tHit > 0) || tHit >= tRemaining) break;

    u0 += vu * tHit;
    v0 = REST_V;
    vv = -(vv - GRAVITY * tHit) * RESTITUTION;
    vu *= FLOOR_FRICTION;
    tRemaining -= tHit;

    u = u0 + vu * tRemaining;
    v = v0 + vv * tRemaining - 0.5 * GRAVITY * tRemaining * tRemaining;
  }

  const landed = v <= REST_V + 0.005;
  if (landed) v = REST_V;

  /* Tumbles while airborne and stops when it lands, because rubble that keeps spinning on
     the ground is the tell that nothing here is simulated. */
  const spinRate = 220 + 260 * hash01(spec.uuid, 6);
  const spinDir = hash01(spec.uuid, 7) < 0.5 ? -1 : 1;

  const fadeFrom = KNOCKOUT_SECONDS - 0.22;
  const alpha = t <= fadeFrom ? 1 : Math.max(0, 1 - (t - fadeFrom) / 0.22);

  return {
    u: clamp(u, -0.98, 0.98),
    v,
    spin: spinDir * spinRate * Math.min(t, KNOCKOUT_SECONDS),
    alpha,
    landed,
  };
}
